import React from 'react';
import {
  InputBase,
  InputLabel,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Field, useField } from 'formik';
import { cx } from '../../helpers/global';
import { important } from '../../utils/global/helper';

interface Props {
  name: string;
  label: string;
  placeholder: string | undefined;
  prependText: string;
  disabled?: boolean;
}

export default function LinkInput({
  name,
  label,
  placeholder,
  prependText,
  disabled,
}: Props) {
  const theme = useTheme();
  const isMediumOrLarger = useMediaQuery(theme.breakpoints.up('md'));

  const [{ value }, { error, touched }, { setTouched, setValue }] =
    useField(name);
  const isError = touched && !!error;

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Get the pressed key character
    const { key } = event;

    // Define a regular expression to match forbidden characters
    const forbiddenChars = /[^a-zA-Z0-9-]/;

    // Prevent the input if the pressed key is a forbidden character or a leading hyphen
    if (
      forbiddenChars.test(key) ||
      (key === '-' && event.currentTarget.selectionStart === 0)
    ) {
      event.preventDefault();
    }
  };

  const onBlur = React.useCallback(() => {
    setTouched(true);
  }, [setTouched]);

  const onChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
    },
    [setValue],
  );

  const errorBorder = '1px solid red';

  return (
    <div
      className={cx('input', 'link', isError && 'error')}
      style={{ position: 'relative' }}>
      <InputLabel htmlFor={name}>
        <Typography variant="h5">{label}</Typography>
      </InputLabel>
      <InputBase
        value={value}
        inputComponent={Field}
        startAdornment={
          prependText ? (
            <Stack
              boxSizing="border-box"
              flexShrink={0}
              justifyContent="center"
              overflow="hidden"
              height="100%"
              px={3}
              maxWidth={{
                xs: '180px',
                sm: 'unset',
              }}
              bgcolor={{
                xs: 'background.paper',
                md: theme.palette.lightGrey.main,
              }}
              borderRight={{
                xs: error
                  ? errorBorder
                  : `1px solid ${theme.palette.outlineGrey.main}`,
                md: 'none',
              }}>
              <Typography
                noWrap
                fontSize={theme.typography.caption.fontSize}
                boxSizing="border-box">
                {prependText}
              </Typography>
            </Stack>
          ) : undefined
        }
        id={name}
        type="text"
        name={name}
        placeholder={placeholder}
        className="link"
        onKeyPress={handleKeyPress}
        onChange={onChange}
        disabled={disabled}
        onBlur={onBlur}
        sx={{
          border: {
            xs: error ? important(errorBorder) : 'none',
            md: important(`1px solid ${theme.palette.lightGrey.main}`),
          },
          boxSizing: 'border-box',
          overflow: 'hidden',
          paddingLeft: important(0),
          paddingRight: important(0),
        }}
        inputProps={{
          style: {
            border: 'none',
            outline: !isMediumOrLarger ? 'none' : error ? undefined : 'none',
            padding: 0,
            boxSizing: 'border-box',
            height: '100%',
            flexGrow: 1,
            flexShrink: 1,
            paddingInline: theme.spacing(prependText ? 2 : 3),
          },
        }}
      />
    </div>
  );
}
